import React, { useState, useRef, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import LabelChip from './LabelChip';
import styles from './AddLabelDropdown.module.css';

interface Label {
  label: string;
  color: string;
}

interface AddLabelDropdownProps {
  availableLabels: Label[];
  existingLabels: Record<string, string>; // Labels already on the note
  position: { 
    x: number; 
    y: number; 
    buttonRect?: {
      top: number;
      bottom: number;
      left: number;
      right: number;
      height: number;
    };
  };
  onAddLabel: (labelName: string, labelColor: string) => void;
  onRemoveLabel?: (labelName: string) => void; // New callback for removing labels
  onClose: () => void;
  onAddLabelToPalette?: (labelName: string, color: string) => Promise<void>;
  onRemoveLabelFromPalette?: (labelName: string) => Promise<void>;
  mode?: 'list' | 'checklist'; // New prop to specify the mode
}

const PRESET_COLORS = [
  '#dc2626', // red (darker)
  '#ea580c', // orange (darker)
  '#ca8a04', // yellow (darker)
  '#16a34a', // green (darker)
  '#059669', // teal (darker)
  '#2563eb', // blue (darker)
  '#4338ca', // indigo (darker)
  '#9333ea', // purple (darker)
  '#db2777', // pink (darker)
  '#4b5563'  // gray (darker)
];

type ViewMode = 'selection' | 'create' | 'manage';

const AddLabelDropdown: React.FC<AddLabelDropdownProps> = ({
  availableLabels,
  existingLabels,
  position,
  onAddLabel,
  onRemoveLabel,
  onClose,
  onAddLabelToPalette,
  onRemoveLabelFromPalette,
  mode = 'list'
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('selection');
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(PRESET_COLORS[0]);
  const [isCreating, setIsCreating] = useState(false);
  const [labelsToRemove, setLabelsToRemove] = useState<Set<string>>(new Set());
  const [isRemoving, setIsRemoving] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Calculate adjusted position to keep dropdown within viewport and avoid button overlap
  const getAdjustedPosition = () => {
    const margin = 16; // 16px margin from browser edge
    const dropdownWidth = 320; // max-width from CSS
    const maxDropdownHeight = 400; // max-height from CSS
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 4; // Gap between button and dropdown
    
    let adjustedX = position.x;
    let positionAbove = false;
    let yPosition = position.y;
    
    // Check if dropdown would go outside right edge
    if (adjustedX + dropdownWidth + margin > viewportWidth) {
      adjustedX = viewportWidth - dropdownWidth - margin;
    }
    
    // Ensure dropdown doesn't go outside left edge
    if (adjustedX < margin) {
      adjustedX = margin;
    }
    
    // Smart vertical positioning to avoid button overlap
    if (position.buttonRect) {
      const buttonRect = position.buttonRect;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      // Check if there's enough space below the button for the max height
      if (spaceBelow >= maxDropdownHeight + gap + margin) {
        // Plenty of space below - position below button
        positionAbove = false;
        yPosition = buttonRect.bottom + gap;
      } else if (spaceAbove >= maxDropdownHeight + gap + margin) {
        // Not enough space below but plenty above - position above button
        positionAbove = true;
        yPosition = buttonRect.top - gap;
      } else {
        // Limited space in both directions - choose the side with more space
        if (spaceBelow > spaceAbove) {
          // More space below
          positionAbove = false;
          yPosition = buttonRect.bottom + gap;
        } else {
          // More space above
          positionAbove = true;
          yPosition = buttonRect.top - gap;
        }
      }
    } else {
      // Fallback to original logic if buttonRect is not available
      // Check if dropdown would go outside bottom edge
      if (yPosition + maxDropdownHeight + margin > viewportHeight) {
        // Try positioning above instead
        if (yPosition - maxDropdownHeight - margin >= 0) {
          positionAbove = true;
          yPosition = yPosition - gap;
        } else {
          // Keep below but adjust position
          yPosition = viewportHeight - maxDropdownHeight - margin;
        }
      }
    }
    
    return { x: adjustedX, y: yPosition, positionAbove };
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInDropdown = dropdownRef.current && dropdownRef.current.contains(target);
      
      if (!isClickInDropdown) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Focus input when in create mode
  useEffect(() => {
    if (viewMode === 'create' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [viewMode]);

  // Filter out labels that are already on the note
  const availableLabelsForNote = availableLabels.filter(
    labelObj => !existingLabels[labelObj.label]
  );

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;

    setIsCreating(true);
    try {
      const labelKey = newLabelName.trim().toLowerCase();
      
      if (onAddLabelToPalette) {
        // Add to palette via API
        await onAddLabelToPalette(labelKey, newLabelColor);
      }

      // Add the label to the note and close dropdown
      onAddLabel(labelKey, newLabelColor);
      onClose();
    } catch (error) {
      console.error('Error creating label:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleRemoveLabel = (labelName: string) => {
    setLabelsToRemove(prev => {
      const newSet = new Set(prev);
      if (newSet.has(labelName)) {
        newSet.delete(labelName);
      } else {
        newSet.add(labelName);
      }
      return newSet;
    });
  };

  const handleBulkRemove = async () => {
    if (!onRemoveLabelFromPalette || labelsToRemove.size === 0) return;

    setIsRemoving(true);
    
    try {
      // Remove all selected labels
      for (const labelName of Array.from(labelsToRemove)) {
        await onRemoveLabelFromPalette(labelName);
      }
      
      // Reset selection and go back to selection mode
      setLabelsToRemove(new Set());
      setViewMode('selection');
    } catch (error) {
      console.error('Error removing labels:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleBackToSelection = () => {
    setViewMode('selection');
    setNewLabelName('');
    setNewLabelColor(PRESET_COLORS[0]);
    setLabelsToRemove(new Set());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreateLabel();
    } else if (e.key === 'Escape') {
      handleBackToSelection();
    }
  };

  const adjustedPosition = getAdjustedPosition();

  return (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      style={{
        position: 'fixed',
        left: adjustedPosition.x,
        ...(adjustedPosition.positionAbove 
          ? { bottom: window.innerHeight - adjustedPosition.y }
          : { top: adjustedPosition.y }
        ),
        zIndex: 1000
      }}
    >
      <div className={styles.dropdownHeader}>
        <div className={styles.titleWithIcon}>
          {viewMode === 'selection' && (
            <>
              <i className={mode === 'checklist' ? 'bi bi-check2-square' : 'bi bi-tag'}></i>
              <span className={styles.title}>{mode === 'checklist' ? 'Edit Labels' : 'Add Label'}</span>
            </>
          )}
          {viewMode === 'create' && (
            <>
              <i className="bi bi-plus-square"></i>
              <span className={styles.title}>Create New Label</span>
            </>
          )}
          {viewMode === 'manage' && (
            <>
              <i className="bi bi-sliders"></i>
              <span className={styles.title}>Manage Labels Palette</span>
            </>
          )}
        </div>
        {viewMode !== 'selection' && (
          <button
            className={styles.backButton}
            onClick={(e) => {
              e.stopPropagation();
              handleBackToSelection();
            }}
            title="Back to label selection"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
        )}
      </div>

      {viewMode === 'selection' && (
        <>
          <div className={styles.labelsList}>
            {mode === 'checklist' ? (
              // Checklist mode: show all labels with checkboxes
              availableLabels.length > 0 ? (
                [...availableLabels]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((labelObj) => {
                    const isChecked = !!existingLabels[labelObj.label];
                    return (
                      <label 
                        key={labelObj.label} 
                        className={styles.checklistLabelItem}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (isChecked && onRemoveLabel) {
                              onRemoveLabel(labelObj.label);
                            } else {
                              onAddLabel(labelObj.label, labelObj.color);
                            }
                          }}
                          className={styles.checkbox}
                        />
                        <LabelChip label={labelObj.label} color={labelObj.color} size="small" />
                      </label>
                    );
                  })
              ) : (
                <div className={styles.noLabelsAvailable}>
                  <p>No labels available yet</p>
                  <p className={styles.emptyStateHint}>Click "Create new" to add your first label</p>
                </div>
              )
            ) : (
              // List mode: show only unused labels as clickable items
              availableLabelsForNote.length > 0 ? (
                availableLabelsForNote.map((labelObj) => (
                  <button
                    key={labelObj.label}
                    className={styles.labelItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddLabel(labelObj.label, labelObj.color);
                      onClose();
                    }}
                  >
                    <LabelChip label={labelObj.label} color={labelObj.color} size="small" />
                  </button>
                ))
              ) : (
                <div className={styles.noLabelsAvailable}>
                  {availableLabels.length === 0 ? (
                    <>
                      <p>No labels available yet</p>
                      <p className={styles.emptyStateHint}>Click "Create new" to add your first label</p>
                    </>
                  ) : (
                    <p>All available labels are already used</p>
                  )}
                </div>
              )
            )}
          </div>
          
          <div className={styles.actionsSection}>
            <button
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                setViewMode('create');
              }}
            >
              <i className="bi bi-plus-circle"></i>
              Create new
            </button>
            
            {onRemoveLabelFromPalette && (
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode('manage');
                }}
              >
                <i className="bi bi-gear"></i>
                Manage labels palette
              </button>
            )}
          </div>
        </>
      )}

      {viewMode === 'manage' && (
        <>
          <div className={styles.labelsList}>
            {availableLabels.length > 0 ? (
              [...availableLabels]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((labelObj) => (
                  <label 
                    key={labelObj.label} 
                    className={styles.manageLabelItem}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={labelsToRemove.has(labelObj.label)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleRemoveLabel(labelObj.label);
                      }}
                      className={styles.checkbox}
                      disabled={isRemoving}
                    />
                    <LabelChip label={labelObj.label} color={labelObj.color} size="small" />
                  </label>
                ))
            ) : (
              <div className={styles.noLabelsAvailable}>
                <p>No labels available yet</p>
                <p className={styles.emptyStateHint}>Go back and click "Create new" to add your first label</p>
              </div>
            )}
          </div>
          
          <div className={styles.actionsSection}>
            <button
              className={styles.removeSelectedButton}
              onClick={(e) => {
                e.stopPropagation();
                handleBulkRemove();
              }}
              disabled={isRemoving || labelsToRemove.size === 0}
            >
              {isRemoving ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Removing...
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  {labelsToRemove.size > 0 
                    ? `Remove selected (${labelsToRemove.size})`
                    : 'Remove selected'
                  }
                </>
              )}
            </button>
          </div>
        </>
      )}

      {viewMode === 'create' && (
        <div className={styles.createNewForm}>
          <input
            ref={inputRef}
            type="text"
            value={newLabelName}
            onChange={(e) => setNewLabelName(e.target.value)}
            onKeyDown={handleKeyPress}
            onClick={(e) => e.stopPropagation()}
            placeholder="Label name"
            className={styles.labelInput}
            maxLength={20}
          />
          
          <div className={styles.colorPicker}>
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                className={`${styles.colorOption} ${newLabelColor === color ? styles.selected : ''}`}
                style={{ backgroundColor: color }}
                onClick={(e) => {
                  e.stopPropagation();
                  setNewLabelColor(color);
                }}
                type="button"
              />
            ))}
          </div>

          <div className={styles.createActions}>
            <button
              className={styles.cancelButton}
              onClick={(e) => {
                e.stopPropagation();
                handleBackToSelection();
              }}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              className={styles.createButton}
              onClick={(e) => {
                e.stopPropagation();
                handleCreateLabel();
              }}
              disabled={!newLabelName.trim() || isCreating}
            >
              {isCreating ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Creating...
                </>
              ) : (
                'Create & Add'
              )}
            </button>
          </div>
        </div>
      )}


    </div>
  );
};

export default AddLabelDropdown; 